/**
 * WTE OMS API Service
 * 
 * This module provides a secure API client for communicating with the backend.
 * All AWS operations are now handled server-side via Lambda functions,
 * eliminating the need for AWS credentials in the frontend.
 */
import { auth } from '../firebase/firebaseConfig';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://your-api-id.execute-api.ap-south-1.amazonaws.com/v1';

/**
 * Handle API response and throw errors for non-2xx status codes
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage = 'API request failed';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  
  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Make an API request with standard headers.
 * Attaches the current Firebase ID token as a Bearer token when a user is
 * logged in, so backend Lambdas can verify who is calling.
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  try {
    if (auth?.currentUser) {
      const idToken = await auth.currentUser.getIdToken();
      headers['Authorization'] = `Bearer ${idToken}`;
    }
  } catch (err) {
    console.warn('[apiService] Could not attach auth token:', err.message);
  }

  const response = await fetch(url, {
    ...options,
    headers
  });

  return handleResponse(response);
};

// ============================================
// SITES API
// ============================================

/**
 * Get all sites
 */
export const getAllSites = async () => {
  try {
    const result = await apiRequest('/sites');
    // Ensure we always return an array
    return Array.isArray(result) ? result : [];
  } catch (error) {
    console.error('[apiService] Error in getAllSites:', error);
    return []; // Return empty array on error
  }
};

/**
 * Get site by ID
 */
export const getSiteById = async (siteId) => {
  return apiRequest(`/sites/${encodeURIComponent(siteId)}`);
};

/**
 * Get site with instruments (alias for getSiteById)
 */
export const getSiteWithInstruments = async (projectId) => {
  return getSiteById(projectId);
};

/**
 * Get site instruments
 */
export const getSiteInstruments = async (projectId) => {
  const site = await getSiteById(projectId);
  return site?.instruments || [];
};

/**
 * Create a new site
 */
export const saveSiteToDynamoDB = async (siteData) => {
  return apiRequest('/sites', {
    method: 'POST',
    body: JSON.stringify(siteData)
  });
};

/**
 * Update a site
 */
export const updateSite = async (siteId, siteData) => {
  return apiRequest(`/sites/${encodeURIComponent(siteId)}`, {
    method: 'PUT',
    body: JSON.stringify(siteData)
  });
};

/** Update only site status (Active | Ongoing). Admin Sites page. */
export const updateSiteStatus = async (siteId, status) => {
  return updateSite(siteId, { status });
};

/**
 * Delete a site
 */
export const deleteSiteFromDynamoDB = async (siteId) => {
  return apiRequest(`/sites/${encodeURIComponent(siteId)}`, {
    method: 'DELETE'
  });
};

// ============================================
// EQUIPMENT API (Instruments, Pumps, Valves, etc.)
// ============================================

/**
 * Add equipment to site (generic function)
 */
const addEquipmentToSite = async (projectId, action, data) => {
  return apiRequest(`/sites/${encodeURIComponent(projectId)}?action=${action}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

/**
 * Update equipment in site (generic function)
 */
const updateEquipmentInSite = async (projectId, action, data) => {
  return apiRequest(`/sites/${encodeURIComponent(projectId)}?action=${action}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

/**
 * Delete equipment from site (generic function)
 */
const deleteEquipmentFromSite = async (projectId, action, itemId) => {
  return apiRequest(`/sites/${encodeURIComponent(projectId)}?action=${action}`, {
    method: 'PUT',
    body: JSON.stringify({ id: itemId })
  });
};

// Instruments
export const addInstrumentToSite = (projectId, instrumentData) => 
  addEquipmentToSite(projectId, 'addInstrument', instrumentData);

export const updateInstrumentInSite = (projectId, instrument) => 
  updateEquipmentInSite(projectId, 'updateInstrument', instrument);

export const deleteInstrumentFromSite = (projectId, instrumentId) => 
  deleteEquipmentFromSite(projectId, 'deleteInstrument', instrumentId);

// Pumps
export const addPumpToSite = (projectId, pumpData) => 
  addEquipmentToSite(projectId, 'addPump', pumpData);

export const updatePumpInSite = (projectId, pump) => 
  updateEquipmentInSite(projectId, 'updatePump', pump);

export const deletePumpFromSite = (projectId, pumpId) => 
  deleteEquipmentFromSite(projectId, 'deletePump', pumpId);

// Blowers
export const addBlowerToSite = (projectId, blowerData) => 
  addEquipmentToSite(projectId, 'addBlower', blowerData);

export const updateBlowerInSite = (projectId, blower) => 
  updateEquipmentInSite(projectId, 'updateBlower', blower);

export const deleteBlowerFromSite = (projectId, blowerId) => 
  deleteEquipmentFromSite(projectId, 'deleteBlower', blowerId);

// Valves
export const addValveToSite = (projectId, valveData) => 
  addEquipmentToSite(projectId, 'addValve', valveData);

export const updateValveInSite = (projectId, valve) => 
  updateEquipmentInSite(projectId, 'updateValve', valve);

export const deleteValveFromSite = (projectId, valveId) => 
  deleteEquipmentFromSite(projectId, 'deleteValve', valveId);

// Switches
export const addSwitchToSite = (projectId, switchData) => 
  addEquipmentToSite(projectId, 'addSwitch', switchData);

export const updateSwitchInSite = (projectId, switchItem) => 
  updateEquipmentInSite(projectId, 'updateSwitch', switchItem);

export const deleteSwitchFromSite = (projectId, switchId) => 
  deleteEquipmentFromSite(projectId, 'deleteSwitch', switchId);

// Dosing Pumps
export const addDosingPumpToSite = (projectId, dosingPumpData) => 
  addEquipmentToSite(projectId, 'addDosingPump', dosingPumpData);

export const updateDosingPumpInSite = (projectId, dosingPump) => 
  updateEquipmentInSite(projectId, 'updateDosingPump', dosingPump);

export const deleteDosingPumpFromSite = (projectId, dosingPumpId) => 
  deleteEquipmentFromSite(projectId, 'deleteDosingPump', dosingPumpId);

// Agitators/Mixers
export const addAgitatorMixerToSite = (projectId, agitatorMixerData) => 
  addEquipmentToSite(projectId, 'addAgitatorMixer', agitatorMixerData);

export const updateAgitatorMixerInSite = (projectId, agitatorMixer) => 
  updateEquipmentInSite(projectId, 'updateAgitatorMixer', agitatorMixer);

export const deleteAgitatorMixerFromSite = (projectId, agitatorMixerId) => 
  deleteEquipmentFromSite(projectId, 'deleteAgitatorMixer', agitatorMixerId);

// Belts/Screens
export const addBeltScreenToSite = (projectId, beltScreenData) => 
  addEquipmentToSite(projectId, 'addBeltScreen', beltScreenData);

export const updateBeltScreenInSite = (projectId, beltScreen) => 
  updateEquipmentInSite(projectId, 'updateBeltScreen', beltScreen);

export const deleteBeltScreenFromSite = (projectId, beltScreenId) => 
  deleteEquipmentFromSite(projectId, 'deleteBeltScreen', beltScreenId);

// Charts
export const addChartToSite = (projectId, chartData) => 
  addEquipmentToSite(projectId, 'addChart', chartData);

export const updateChartInSite = (projectId, chartData) => 
  updateEquipmentInSite(projectId, 'updateChart', chartData);

export const deleteChartFromSite = (projectId, chartId) => 
  deleteEquipmentFromSite(projectId, 'deleteChart', chartId);

// Gateway
export const addGatewayToSite = (projectId, gatewayData) => 
  apiRequest(`/sites/${encodeURIComponent(projectId)}?action=updateGateway`, {
    method: 'PUT',
    body: JSON.stringify(gatewayData)
  });

// Canvas Layout
export const saveCanvasLayout = (projectId, canvasData) => 
  apiRequest(`/sites/${encodeURIComponent(projectId)}?action=saveCanvas`, {
    method: 'PUT',
    body: JSON.stringify(canvasData)
  });

export const getCanvasLayout = async (projectId) => {
  const site = await getSiteById(projectId);
  return site?.canvasLayout || {
    nodes: [],
    edges: [],
    lastModified: null,
    version: '1.0',
    metadata: {
      totalNodes: 0,
      totalEdges: 0,
      canvasVersion: '1.0'
    }
  };
};

export const updateCanvasLayout = saveCanvasLayout;

export const deleteCanvasLayout = async (projectId) => {
  return apiRequest(`/sites/${encodeURIComponent(projectId)}`, {
    method: 'PUT',
    body: JSON.stringify({ canvasLayout: null })
  });
};

// ============================================
// USERS API
// ============================================

/**
 * Get all users
 */
export const getAllUsers = async () => {
  try {
    const result = await apiRequest('/users');
    // Ensure we always return an array
    return Array.isArray(result) ? result : [];
  } catch (error) {
    console.error('[apiService] Error in getAllUsers:', error);
    return []; // Return empty array on error
  }
};

/**
 * Get user by email
 */
export const getUserFromDynamoDB = async (email) => {
  return apiRequest(`/users/${encodeURIComponent(email)}`);
};

/**
 * Create a new user
 */
export const saveUserToDynamoDB = async (userData) => {
  return apiRequest('/users', {
    method: 'POST',
    body: JSON.stringify({
      email: userData.email,
      uid: userData.uid,
      name: userData.displayName || userData.name || 'No Name',
      photoURL: userData.photoURL || '',
      role: 'User',
      site: {
        name: '',
        id: '',
        assignedAt: null
      }
    })
  });
};

/**
 * Update user
 */
export const updateUserInDynamoDB = async (email, updatedFields) => {
  return apiRequest(`/users/${encodeURIComponent(email)}`, {
    method: 'PUT',
    body: JSON.stringify({ updateFields: updatedFields })
  });
};

/**
 * Update user role
 */
export const updateUserRole = async (userEmail, newRole) => {
  return updateUserInDynamoDB(userEmail, { role: newRole || 'User' });
};

/**
 * Update user site assignment (single site)
 */
export const updateUserSiteAssignment = async (userEmail, siteName, siteId) => {
  return updateUserInDynamoDB(userEmail, { 
    site: { name: siteName, id: siteId } 
  });
};

/**
 * Update user sites assignment (multiple sites)
 */
export const updateUserSitesAssignment = async (userEmail, sitesArray) => {
  return updateUserInDynamoDB(userEmail, { sites: sitesArray });
};

/**
 * Delete user
 */
export const deleteUser = async (userEmail) => {
  return apiRequest(`/users/${encodeURIComponent(userEmail)}`, {
    method: 'DELETE'
  });
};

// ============================================
// TELEMETRY API
// ============================================

/**
 * Get live telemetry data
 */
export const getTelemetryData = async (projectId, tagIds = []) => {
  // DynamoDB BatchGet rejects duplicate keys — always send unique tagIds
  const uniqueTagIds = [...new Set(
    (tagIds || []).map((t) => String(t).trim()).filter(Boolean)
  )];

  console.log('[TelemetryService] Fetching data for:', {
    projectId,
    tagIds: uniqueTagIds,
    timestamp: new Date().toISOString()
  });

  const params = new URLSearchParams({ projectId });
  if (uniqueTagIds.length > 0) {
    params.append('tagIds', uniqueTagIds.join(','));
  }

  const result = await apiRequest(`/telemetry/live?${params}`);
  
  console.log('[TelemetryService] Received response:', {
    totalItems: result.length,
    items: result.map(item => ({
      tagId: item.tagId,
      value: item.value,
      timestamp: item.timestamp
    }))
  });

  return result;
};

/**
 * Get telemetry history — prefers dedicated telemetry API (legacy) when configured,
 * then falls back to main API Gateway. History is often only on the telemetry API.
 */
export const getTelemetryHistory = async (params) => {
  const queryParams = new URLSearchParams(params);
  const legacyBase = process.env.REACT_APP_TELEMETRY_API_URL;

  const fetchLegacy = async () => {
    if (!legacyBase) return null;
    const legacyUrl = `${legacyBase.replace(/\/$/, '')}/telemetry/history/?${queryParams}`;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), 25000)
      : null;
    try {
      const response = await fetch(legacyUrl, controller ? { signal: controller.signal } : undefined);
      if (!response.ok) {
        throw new Error(`Legacy history HTTP ${response.status}`);
      }
      return response.json();
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  if (legacyBase) {
    try {
      return await fetchLegacy();
    } catch (legacyError) {
      console.warn('[apiService] Legacy history failed, trying main API:', legacyError.message);
    }
  }

  return apiRequest(`/telemetry/history?${queryParams}`);
};

/**
 * Get gateway heartbeat status for a site (router-generated heartbeat only).
 * Returns null when the endpoint is not deployed or the site has no heartbeat yet.
 */
export const getGatewayStatus = async (projectId) => {
  if (!projectId) return null;

  try {
    const params = new URLSearchParams({ projectId });
    return await apiRequest(`/gateway/status?${params}`);
  } catch (error) {
    console.warn('[apiService] Gateway status unavailable:', error.message);
    return null;
  }
};

/**
 * Get tag names by project ID
 */
export const getTagNamesByProjectId = async (projectId) => {
  const data = await apiRequest(`/telemetry/live?projectId=${encodeURIComponent(projectId)}`);
  const tagNames = [...new Set(data.map(item => item.tagName))];
  return tagNames;
};

/**
 * Get tag IDs by project ID
 */
export const getTagIdsByProjectId = async (projectId) => {
  const data = await apiRequest(`/telemetry/live?projectId=${encodeURIComponent(projectId)}`);
  const tagIds = [...new Set(data.map(item => item.tagId))];
  return tagIds;
};

// ============================================
// ACTIVITY API
// ============================================

/**
 * Log user activity
 */
export const logActivity = async (userEmail, action = 'Logged In') => {
  console.log('Invoking logActivity', { userEmail, action });
  
  return apiRequest('/activity', {
    method: 'POST',
    body: JSON.stringify({ userEmail, action })
  });
};

/**
 * Fetch activity logs
 */
export const fetchActivityLogs = async (selectedDate) => {
  console.log('Invoking fetchActivityLogs for date:', selectedDate);
  
  const params = selectedDate ? `?date=${selectedDate}` : '';
  return apiRequest(`/activity${params}`);
};

/**
 * Get activity logs (alias for fetchActivityLogs without date filter)
 */
export const getActivityLogs = async () => {
  return apiRequest('/activity');
};

// ============================================
// NOTIFICATIONS API
// ============================================

/**
 * Send welcome email
 */
export const sendWelcomeEmail = async (email, fullName) => {
  return apiRequest('/notifications', {
    method: 'POST',
    body: JSON.stringify({ 
      type: 'welcome', 
      email, 
      fullName 
    })
  });
};

/**
 * Send user signup details (admin notification)
 */
export const sendUserSignupDetails = async ({ fullName, companyName, email }) => {
  return apiRequest('/notifications', {
    method: 'POST',
    body: JSON.stringify({ 
      type: 'signup', 
      email, 
      fullName, 
      companyName 
    })
  });
};

/**
 * Send custom alert notification
 */
export const sendAlertNotification = async (subject, message) => {
  return apiRequest('/notifications', {
    method: 'POST',
    body: JSON.stringify({ 
      type: 'alert', 
      subject, 
      message 
    })
  });
};

// ============================================
// FORUM API
// ============================================

/**
 * Get forum posts by project ID
 */
export const getForumPostsByProjectId = async (projectId) => {
  const site = await getSiteById(projectId);
  return site?.forumPosts || [];
};

/**
 * Add forum post
 */
export const addForumPost = async (projectId, post) => {
  const newPost = {
    id: Date.now().toString(),
    ...post,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  return addEquipmentToSite(projectId, 'addForumPost', newPost);
};

/**
 * Update forum post
 */
export const updateForumPost = async (projectId, postId, updates) => {
  const updatedPost = {
    id: postId,
    ...updates,
    updatedAt: new Date().toISOString()
  };
  
  return updateEquipmentInSite(projectId, 'updateForumPost', updatedPost);
};

/**
 * Delete forum post
 */
export const deleteForumPost = async (projectId, postId) => {
  return deleteEquipmentFromSite(projectId, 'deleteForumPost', postId);
};

/**
 * Add reply to forum post
 */
export const addReplyToPost = async (projectId, postId, replyData) => {
  return apiRequest(`/sites/${encodeURIComponent(projectId)}?action=addReply`, {
    method: 'PUT',
    body: JSON.stringify({
      postId,
      content: replyData.content,
      author: replyData.author
    })
  });
};

// ============================================
// EXPORT DATA API
// ============================================

/**
 * Export telemetry data to CSV
 */
export const exportTelemetryData = async (params) => {
  const queryParams = new URLSearchParams(params);
  const response = await fetch(`${API_BASE_URL}/export-telemetry?${queryParams}`);
  
  if (!response.ok) {
    throw new Error('Failed to export data');
  }
  
  return response.text();
};

